import { lazy, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

const DashboardPreview = lazy(
  () => import("../common/components/Embeded-Dashboards/DashboardPreview"),
);
const AffiliateMaps = lazy(() => import("../modules/Admin/Maps"));
const DashboardPage = lazy(() => import("../modules/Dashboard"));
const ProfilePage = lazy(() => import("../modules/Profile"));
const RegistrationPage = lazy(
  () => import("../public/auth/registration/RegistrationPage"),
);
const SignupWithPHPInModal = lazy(() => import("../public/auth/sign-up"));
import { setPatientName } from "../redux/slices/ehr/patientCharting.slice";
import { reset } from "../redux/slices/ehr/scheduling.slice";
import { setSelectedTabView } from "../redux/slices/ehr/visitnote.slice";
import { RootState } from "../redux/store/store";
import AdminRoutes from "./adminRoutes";
import AffiliatePortalRoutes from "./affiliatePortalRoutes";
import BillingRoutes from "./billingRoutes";
import BrokerPortalRoutes from "./brokerPortalRoutes";
import CrmRoutes from "./crmRoutes";
import EhrRoutes from "./ehrRoutes";
import EmployerManagementRoutes from "./employerManagementRoutes";
import EmployerPortalRoutes from "./employerPortalRoutes";
import InventoryRoutes from "./inventoryRoutes";
import AuthLayout from "./layouts/auth-layout";
import MainLayout from "./layouts/main-layout";
import PrivateRoute from "./private-route";
import PublicRoute from "./public-route";
import ReportsRoutes from "./reportsRoutes";
import { useUserControllerServiceGetProfile } from "../services/queries";

const AuthRoute = () => (
  <PublicRoute>
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  </PublicRoute>
);

const MapsRoute = () => (
  <PublicRoute>
    <AffiliateMaps />
  </PublicRoute>
);

const MainRoute = () => (
  <PrivateRoute>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </PrivateRoute>
);

const DashboardRoute = () => (
  <PrivateRoute>
    <Outlet />
  </PrivateRoute>
);

const RoleBasedRedirect = ({ fallback }: { fallback?: JSX.Element }) => {
  const { data } = useUserControllerServiceGetProfile([], {
    staleTime: Infinity,
  });
  const profile: any = data?.data;

  if (!profile?.roleType) {
    return <div />;
  }

  if (profile.roleType === "AGENT" || profile.roleType === "BROKER") {
    return (
      <Navigate to={`/broker-portal/agent/${profile.uuid}/dashboard`} replace />
    );
  }

  if (profile.roleType === "EMPLOYER") {
    return (
      <Navigate
        to={`/employer-portal/employers/${profile.employerUuid}/dashboard`}
        replace
      />
    );
  }

  if (profile.roleType === "AFFILIATE") {
    return (
      <Navigate
        to={`/affiliate-portal/affiliates/${profile.affiliateGroupUuid}/dashboard`}
        replace
      />
    );
  }
  return fallback ?? <Navigate to="/dashboard" replace />;
};
const AppRouter = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const isAffiliateRole = user?.roleType === "AFFILIATE";
  useEffect(() => {
    if (!location?.pathname?.includes("scheduling")) {
      dispatch(reset());
      dispatch(setSelectedTabView(null));
    }
    const isPatientPath = location?.pathname?.includes("/ehr/patients");
    if (!isPatientPath) {
      dispatch(setPatientName(null));
    }
  }, [location]);
  return (
    <Suspense
      fallback={
        <div
          style={{
            minHeight: "100vh",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      }
    >
      <Routes>
        <Route path="auth" element={<AuthRoute />}>
          <Route path="register/:uuid" element={<RegistrationPage />} />
          <Route path="sign-up" element={<SignupWithPHPInModal />} />
          {/* Plan-specific entry point — the sign-up link copied from a retail
              plan card. Same component; it reads `planUuid` from useParams. */}
          <Route path="sign-up/:planUuid" element={<SignupWithPHPInModal />} />
        </Route>

        <Route path="maps" element={<MapsRoute />} />

        <Route path="auth/maps" element={<MapsRoute />} />

        <Route path="/" element={<MainRoute />}>
          <Route index element={<RoleBasedRedirect />} />
        </Route>

        <Route path="/dashboard" element={<DashboardRoute />}>
          <Route
            index
            element={<RoleBasedRedirect fallback={<DashboardPage />} />}
          />
        </Route>

        <Route path="/dashboard-preview" element={<DashboardRoute />}>
          <Route path=":dashboardType" element={<DashboardPreview />} />
        </Route>

        <Route path="/profile" element={<MainRoute />}>
          <Route index element={<ProfilePage />} />
        </Route>

        {/* Admin Routes */}
        {AdminRoutes}
        {/* Affiliate Portal Routes */}
        {AffiliatePortalRoutes}

        {/* Broker Portal Routes */}
        {BrokerPortalRoutes}

        {/* CRM Routes */}
        {CrmRoutes}

        {/* EHR Routes */}
        {EhrRoutes}

        {/* Inventory Routes */}
        {isAffiliateRole ? null : InventoryRoutes}

        {/* Billing Routes */}
        {BillingRoutes}

        {/* Employer Management Routes */}
        {EmployerManagementRoutes}

        {/* Employer Portal Routes */}
        {EmployerPortalRoutes}

        {/* Reports Routes */}
        {ReportsRoutes}

        {/* Catch all route */}
        <Route path="*" element={<RoleBasedRedirect />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
